/**
* Campaigns Routes
*/
const CampaignRoutes = {
  base: '/campaigns',
  campaign: (id) => `/campaigns/${id}`,
  campaignCopy: (id) => `/campaigns/${id}/copy`,
  campaignCopyAll: (id) => `/campaigns/${id}/copy-all`,

  campaignResendInvitations: (id) => `/campaigns/${id}/invitations/send`,
  campaignRedeemTypes: '/campaign-access/campaign-redeem-types',

  approveRecipient: (id, recipientId) => `/campaigns/${id}/recipient/${recipientId}/approve`,
  approveAllRecipients: (id) => `/campaigns/${id}/approve-all`,

  campaignPaymentMethods: '/customers/campaign-payment-methods',

  projects: (id) => `/campaigns/${id}/projects`,
  projectPersonalization: (id) => `/projects/${id}/personalization`,

  recipients: (id) => `/campaigns/${id}/recipients`,
  recipient: (id, recipientId) => `/campaigns/${id}/recipients/${recipientId}`,
  recipientCancel: (id, recipientId) => `/campaigns/${id}/recipients/${recipientId}/cancel`,
  recipientsUpload: (id, type) => {
    switch (type) {
    case 'addressesUploader':
      return `/campaigns/${id}/recipients/upload`
    case 'personalizationsUploader':
      return `/campaigns/${id}/upload/personalization`
    }
  },

  downloads: (type) => `/campaigns/downloads/${type}`,
  campaignDownload: (id, type) => {
    switch (type) {
    case 'recipients':
      return `/campaigns/${id}/recipients/download`
    case 'campaignPersonalizations':
      return `/campaigns/${id}/download/personalization`
    default:
      return `/campaigns/${id}/downloads/${type}`
    }
  }
}

/**
* Invalidate Campaigns Cache
* @param {String} id - Optional campaign ID
*/
export const invalidateCampaigns = {
  base() {
    return useInvalidateCache(CampaignRoutes.base)
  },

  campaign(id) {
    if (id) {
      return useInvalidateCache(CampaignRoutes.campaign(id))
    }
    return Promise.resolve()
  },

  projects(id) {
    if (id) {
      return useInvalidateCache(CampaignRoutes.projects(id))
    }
    return Promise.resolve()
  },

  recipient(id, recipientId) {
    if (id && recipientId) {
      return useInvalidateCache(CampaignRoutes.recipient(id, recipientId))
    }
    return Promise.resolve()
  },

  recipients(id) {
    if (id) {
      return useInvalidateCache(CampaignRoutes.recipients(id))
    }
    return Promise.resolve()
  },

  async all(id) {
    await this.base()
    if (id) {
      await this.campaign(id)
      await this.projects(id)
      await this.recipients(id)
    }
  }
}

/**
* useGetCampaigns
* @param {Object} params
* @returns {Promise<Object>}
* @returns Object
*/
export function useGetCampaigns(params) {
  return useCustomFetch(CampaignRoutes.base, {
    headers: {
      'Cache-Control': 's-maxage=60, stale-while-revalidate'
    },
    params: params
  })
}

/**
* useGetCampaign
* @param {String} id
* @returns {Promise<Object>}
*/
export function useGetCampaign(id) {
  return useCustomFetch(CampaignRoutes.campaign(id), {})
}

/**
* useEditCampaign
* @param {String} id
* @param {Object} params
* @returns {Promise<Object>}
*/
export function useEditCampaign(id, params) {
  const { $i18n: i18n } = useNuxtApp()

  return useCustomFetch(CampaignRoutes.campaign(id), {
    method: 'PUT',
    body: params,
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Message
      if (response?._data?.campaign?.status === 'Cancelled') {
        useEventQueue().add('toast', { severity: 'success', detail: (response?._data?.campaign?.type === 'RP') ? i18n?.t('redeemUpdated') : i18n?.t('campaignCanceled'), life: 5000 })
      } else {
        useEventQueue().add('toast', { severity: 'success', detail: (response?._data?.campaign?.type === 'RP') ? i18n?.t('redeemUpdated') : i18n?.t('campaignUpdated'), life: 5000 })
      }

      // Revalidate Campaigns
      await Promise.all([
        invalidateCampaigns.base(),
        invalidateCampaigns.campaign(id),
        invalidateCampaigns.projects(id),
        response?._data?.campaign?.status === 'Cancelled' ? invalidateCampaigns.recipients(id) : Promise.resolve()
      ])

      return response
    }
  }, {
    errorToast: false
  })
}

/**
* useDeleteCampaign
* @param {String} id
* @returns {Promise<Object>}
*/
export function useDeleteCampaign(id) {
  const { $i18n: i18n } = useNuxtApp()

  return useCustomFetch(CampaignRoutes.campaign(id), {
    method: 'DELETE',
    async onResponse({ response }) {
      // Message
      useEventQueue().add('toast', { severity: 'success', detail: i18n?.t('campaignSuccessfullyDeleted'), life: 5000 })

      // Revalidate Campaigns
      await invalidateCampaigns.base()

      return response
    }
  })
}

/**
* useCopyCampaign
* @param {String} id
* @returns {Promise<Object>}
*/
export function useCopyCampaign(id) {
  const { $i18n: i18n } = useNuxtApp()

  return useCustomFetch(CampaignRoutes.campaignCopy(id), {
    method: 'POST',
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Message
      useEventQueue().add('toast', { severity: 'success', detail: i18n?.t('campaignSuccessfullyCopied'), life: 5000 })

      // Revalidate Campaigns
      await invalidateCampaigns.base()

      return response
    }
  })
}

/**
* useCopyCampaignAll
* @param {String} id
* @returns {Promise<Object>}
*/
export function useCopyCampaignAll(id) {
  const { $i18n: i18n } = useNuxtApp()

  return useCustomFetch(CampaignRoutes.campaignCopyAll(id), {
    method: 'POST',
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Revalidate Campaigns
      await invalidateCampaigns.base()

      // Message
      useEventQueue().add('toast', { severity: 'success', detail: i18n?.t('campaignSuccessfullyCopied'), life: 5000 })

      return response
    }
  })
}

/**
* useCreateCampaign
* @param {Object} params
* @returns {Promise<Object>}
*/
export function useCreateCampaign(params) {
  const { $i18n: i18n } = useNuxtApp()

  return useCustomFetch(CampaignRoutes.base, {
    method: 'POST',
    body: params,
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Revalidate Campaigns
      await invalidateCampaigns.base()

      // Message
      useEventQueue().add('toast', {
        severity: 'success', detail: params.type === 'RP'
          ? i18n?.t('redeemSuccessfullyCreated')
          : i18n?.t('campaignSuccessfullyCreated'), life: 5000
      })

      return response
    }
  })
}

/**
* useGetCampaignProjects
* @param {String} id
* @param {Object} params
* @returns {Promise<Object>}
*/
export function useGetCampaignProjects(id, params) {
  return useCustomFetch(CampaignRoutes.projects(id), {
    params: params
  })
}

/**
* useGetProjectPersonalization
* @param {String} id - Project ID
* @param {Object} params
* @returns {Promise<Object>}
*/
export function useGetProjectPersonalization(id, params) {
  return useCustomFetch(CampaignRoutes.projectPersonalization(id), {
    params: params
  })
}

/**
* useGetCampaignRecipients
* @param {String} id
* @param {Object} params
* @returns {Promise<Object>}
*/
export function useGetCampaignRecipients(id, params) {
  return useCustomFetch(CampaignRoutes.recipients(id), {
    params: params})
}

/**
* useCancelCampaignRecipient
* @param {String} id
* @returns {Promise<Objet>}
*/
export function useCancelCampaignRecipient(id, recipientId) {
  const { $i18n: i18n } = useNuxtApp()

  return useCustomFetch(CampaignRoutes.recipientCancel(id, recipientId), {
    method: 'PUT',
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Message
      useEventQueue().add('toast', { severity: 'success', detail: i18n?.t('recipientSuccessfullyCanceled'), life: 5000 })

      // Revalidate Campaigns
      await invalidateCampaigns.base()
      await invalidateCampaigns.campaign(id)
      await invalidateCampaigns.recipients(id)

      return response
    }
  })
}

/**
* useDeleteCampaignRecipient
* @param {String} id
* @returns {Promise<Objet>}
*/
export function useDeleteCampaignRecipient(id, recipientId) {
  const { $i18n: i18n } = useNuxtApp()
  
  return useCustomFetch(CampaignRoutes.recipient(id, recipientId), {
    method: 'DELETE',
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Message
      useEventQueue().add('toast', { severity: 'success', detail: i18n?.t('recipientSuccessfullyDeleted'), life: 5000 })

      // Revalidate Campaigns
      await invalidateCampaigns.base()
      await invalidateCampaigns.campaign(id)
      await invalidateCampaigns.recipients(id)

      return response
    }
  })
}

/**
* useUploadRecipients
* @param {Object} params
* @param {Object} options
* @returns {Promise<Object>}
*/
export async function useUploadRecipients(params, options) {
  return useCustomFetch(CampaignRoutes.recipientsUpload(options.id, options.type), {
    method: 'POST',
    body: params,
    headers: {
      'content-type': false
    },
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Return response
      response._data = {
        success: true,
        data: response?._data?.data
      }

      return response
    }
  }, {
    firceClient: true
  })
}

/**
* useAddRecipient
* @param {String} id
* @param {Object} params
* @returns {Promise<Objet>}
*/
export function useAddRecipient(id, params) {
  const { $i18n: i18n } = useNuxtApp()
  
  return useCustomFetch(CampaignRoutes.recipients(id), {
    method: 'POST',
    body: params,
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Revalidate Campaigns
      await invalidateCampaigns.base()
      await invalidateCampaigns.campaign(id)
      await invalidateCampaigns.recipients(id)

      // Message
      useEventQueue().add('toast', { severity: 'success', detail: i18n?.t('recipientSuccessAdded'), life: 5000 })

      return response
    }
  })
}

/**
* useEditRecipient
* @param {String} id
* @param {Object} params
* @returns {Promise<Objet>}
*/
export function useEditRecipient(id, recipientId, params) {
  return useCustomFetch(CampaignRoutes.recipient(id, recipientId), {
    method: 'PUT',
    body: params,
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Revalidate Campaigns
      await invalidateCampaigns.base()
      await invalidateCampaigns.campaign(id)
      await invalidateCampaigns.recipient(id, recipientId)
      await invalidateCampaigns.recipients(id)

      return response
    }
  }, {
    errorToast: false
  })
}

/**
* useGetCampaignRedeemTypes
* @returns {Promise<Object>}
*/
export function useGetCampaignRedeemTypes() {
  return useCustomFetch(CampaignRoutes.campaignRedeemTypes)
}

/**
* useApproveAllRecipients
* @param {String} id
* @returns {Promise<Object>}
*/
export function useApproveRecipient(id, recipientId, params) {
  const { $i18n: i18n } = useNuxtApp()

  return useCustomFetch(CampaignRoutes.approveRecipient(id, recipientId), {
    method: 'POST',
    body: params,
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Message
      useEventQueue().add('toast', { severity: 'success', detail: i18n?.t('recipientSuccessfullyUpdated'), life: 5000 })

      // Revalidate Campaigns
      await invalidateCampaigns.base()
      await invalidateCampaigns.campaign(id)
      await invalidateCampaigns.recipients(id)

      return response
    }
  })
}

/**
* useApproveAllRecipients
* @param {String} id
* @returns {Promise<Object>}
*/
export function useApproveAllRecipients(id, params) {
  const { $i18n: i18n } = useNuxtApp()

  return useCustomFetch(CampaignRoutes.approveAllRecipients(id), {
    method: 'POST',
    body: params,
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Message
      useEventQueue().add('toast', { severity: 'success', detail: i18n?.t('recipientSuccessfullyUpdated'), life: 5000 })

      // Revalidate Campaigns
      await invalidateCampaigns.base()
      await invalidateCampaigns.campaign(id)
      await invalidateCampaigns.recipients(id)

      return response
    }
  })
}

/**
* useResendInvitations
* @param {String} id
* @returns {Promise<Object>}
*/
export function useResendInvitations(id) {
  const { $i18n: i18n } = useNuxtApp()
  
  return useCustomFetch(CampaignRoutes.campaignResendInvitations(id), {
    method: 'POST',
    async onResponse({ response }) {
      if (response?._data?.error) return response

      // Message
      useEventQueue().add('toast', { severity: 'success', detail: i18n?.t('sendReferralSuccess'), life: 5000 })

      return response
    }
  })
}

/**
* useGetCampaignPaymentMethods
* @returns {Promise<Object>}
*/
export function useGetCampaignPaymentMethods() {
  return useCustomFetch(CampaignRoutes.campaignPaymentMethods)
}

/**
* useGetCampaignsDownloads
* @param {String} type
* @param {String} id (optional)
* @returns {Promise<Blob>}
*/
export function useGetCampaignsDownloads(type, id) {
  const params = {}

  if (id) {
    params.id = id
  }

  let paramsString = new URLSearchParams(params).toString()
  if (paramsString) {
    paramsString = '?' + paramsString
  }

  const link = id
    ? CampaignRoutes.campaignDownload(id, type)
    : CampaignRoutes.downloads(type)

  return useCustomFetch(`${link}${paramsString}`, {
    responseType: 'blob'
  }, {
    imperativeFetch: true
  })
}
